import { inject, injectable, multiInject } from '@patsnap/tahiti-core/shared/inversify';
import type { TranslateStageController } from '../translate-stage-controller';
import { GetTranslateServerSideStateService } from '../get-translate-task-status-service';
import { TranslateServerSideModel } from '../../models/translate-server-side-model';
import { TranslateStage } from '../translate-stage';
import { RestartTranslateTaskApi } from '@/infrastructure/api-functions/core-drafting-api/restart-translate-task-api';

@injectable()
export class TranslateStageControllerImpl implements TranslateStageController {
    // eslint-disable-next-line max-params
    constructor(
        @inject(GetTranslateServerSideStateService)
        private readonly _getTranslateServerSideStateService: GetTranslateServerSideStateService,
        @inject(TranslateServerSideModel)
        private readonly _translateServerSideModel: TranslateServerSideModel,
        @multiInject(TranslateStage)
        private readonly _translateStages: TranslateStage[],
        @inject(RestartTranslateTaskApi)
        private readonly _restartTranslateTaskApi: RestartTranslateTaskApi,
    ) {}

    get currentStage(): TranslateStage {
        return this._translateStages.find((stage) =>
            stage.isCurrentStage(this._translateServerSideModel),
        ) as TranslateStage;
    }

    get previousStage(): TranslateStage {
        return this._translateStages.find((stage) =>
            stage.isPreviousStage(this._translateServerSideModel),
        ) as TranslateStage;
    }

    get nextStage(): TranslateStage {
        return this._translateStages.find((stage) =>
            stage.isNextStage(this._translateServerSideModel),
        ) as TranslateStage;
    }

    async restart(taskId: string): Promise<void> {
        await this._restartTranslateTaskApi.restartTranslateTask({ task_id: taskId });
        this.init(taskId);
    }

    async init(taskId: string): Promise<void> {
        await this._getTranslateServerSideStateService.pullTranslateServerSideState(taskId);
        /**
         * 进入当前阶段
         */
        this._enterCurrentStage();
    }

    /**
     * 进入当前阶段
     */
    private _enterCurrentStage() {
        const stage = this.currentStage;
        if (stage) {
            stage.onEnter(this._translateServerSideModel);
        }
    }

    async next(): Promise<void> {
        await this._executeCurrentStageLeaveHook();
        this._enterNextStage();
    }

    /**
     * 执行当前阶段离开 hook
     */
    private _executeCurrentStageLeaveHook() {
        const stage = this.currentStage;
        if (stage) {
            return stage.onLeave();
        }
    }

    /**
     * 进入下一个阶段
     */
    private _enterNextStage() {
        const stage = this.nextStage;
        if (stage) {
            stage.onEnter(this._translateServerSideModel);
        }
    }

    async prev(): Promise<void> {
        try {
            await this._executeCurrentStageLeaveHook();
            this._enterPreviousStage();
        }
        catch (error) {
            console.error(error);
        }
    }

    /**
     * 进入上一个阶段
     */
    private _enterPreviousStage() {
        const previousStage = this.previousStage;

        if (previousStage) {
            previousStage.onEnter(this._translateServerSideModel);
        }
    }
}
